import React, { Component } from 'react';
import { Button, Checkbox, Form, Grid, Icon, Message, Modal, Select } from 'semantic-ui-react';
import { ErrorMessage, Field, FieldProps, Formik } from 'formik';
import { Dropdown } from 'formik-semantic-ui';
import { connect } from 'react-redux';
import { EhsMaintenanceRoles, EhsSiteRoles, EUserLevels, EUserRoles, IUser } from '@relab/alstom-ptw-shared';
import i18n from 'i18next';

import { SignUpValues } from '../../interfaces';
import { ActionType, RootState, ServerRequestStatus } from '../../redux/types';
import { appStateServerRequests } from '../../redux/selectors';
import { userEditAction } from '../../redux/actions';
import { TranslationComponent } from '../TranslationComponent';
import { withTranslation } from 'react-i18next';
import { CreateUserSchema } from './UserSchema';

interface UserEditModalProps {
  i18n: typeof i18n;
  t: typeof i18n.t;
  user: IUser;
  userEditAction: any;
  serverRequest?: ServerRequestStatus;
}

class PUserEditModal extends Component<UserEditModalProps> {
  state = { modalOpen: false };

  handleOpen = () => this.setState({ modalOpen: true });

  handleClose = () => this.setState({ modalOpen: false });

  handleUserRoleChange = (
    userRoles: EUserRoles[],
    setFieldValue: any,
    userRoleValue: EUserRoles,
    selected: boolean
  ) => {
    if (EhsSiteRoles.includes(userRoleValue)) {
      userRoles = userRoles.filter((uR) => !EhsSiteRoles.includes(uR));
    }

    if (EhsMaintenanceRoles.includes(userRoleValue)) {
      userRoles = userRoles.filter((uR) => !EhsMaintenanceRoles.includes(uR));
    }

    if (selected) {
      setFieldValue('userRoles', [...userRoles, userRoleValue]);
    } else {
      setFieldValue(
        'userRoles',
        userRoles.filter((uR) => uR !== userRoleValue)
      );
    }
  };

  handleSubmit = (values: SignUpValues) => {
    const { user } = this.props;

    this.props.userEditAction({
      ...user,
      ...values,
      password: values.password === '' ? user.password : values.password,
    });
  };

  render() {
    const { user, serverRequest, t } = this.props;

    return (
      <Formik
        initialValues={{
          name: user.name,
          email: user.email,
          password: '',
          userLevel: user.userLevel,
          userRoles: user.userRoles || [],
        }}
        validationSchema={CreateUserSchema(t, true)}
        onSubmit={this.handleSubmit}
      >
        {({ handleSubmit, isSubmitting, values, setFieldValue }) => (
          <Modal
            trigger={
              <Button icon onClick={this.handleOpen}>
                <Icon name="pencil alternate" />
              </Button>
            }
            open={this.state.modalOpen}
            onClose={this.handleClose}
            closeOnDimmerClick={false}
          >
            <Modal.Header>
              <TranslationComponent stringKey={'Edit User'} />
            </Modal.Header>
            <Modal.Content>
              <Form
                id="editUserForm"
                size="large"
                error
                loading={isSubmitting && serverRequest && serverRequest.pending}
                onSubmit={handleSubmit}
              >
                <Modal.Description>
                  <Field name="name">
                    {({ field }: FieldProps) => (
                      <Form.Input placeholder={t('Name')} fluid icon="user" iconPosition="left" {...field} />
                    )}
                  </Field>
                  <ErrorMessage name="name">{(msg) => <Message error content={msg} />}</ErrorMessage>
                  <Field name="email">
                    {({ field }: FieldProps) => (
                      <Form.Input placeholder={t('E-mail address')} fluid icon="mail" iconPosition="left" {...field} />
                    )}
                  </Field>
                  <ErrorMessage name="email">{(msg) => <Message error content={msg} />}</ErrorMessage>
                  <Field name="password">
                    {({ field }: FieldProps) => (
                      <Form.Input
                        fluid
                        icon="lock"
                        iconPosition="left"
                        placeholder={t('Password')}
                        type="password"
                        {...field}
                      />
                    )}
                  </Field>
                  <ErrorMessage name="password">{(msg) => <Message error content={msg} />}</ErrorMessage>
                  <Field name="userLevel">
                    {() => (
                      <Dropdown
                        name="userLevel"
                        options={[
                          { text: t('Standard'), value: EUserLevels.User },
                          { text: t('Admin'), value: EUserLevels.Admin },
                        ]}
                      />
                    )}
                  </Field>
                  <Grid columns={'equal'}>
                    <Grid.Row>
                      <Grid.Column>
                        <Checkbox
                          label={t('PTW')}
                          checked={values.userRoles.includes(EUserRoles.Ptw)}
                          onClick={(event, data) =>
                            this.handleUserRoleChange(values.userRoles, setFieldValue, EUserRoles.Ptw, data.checked)
                          }
                        />
                      </Grid.Column>
                      <Grid.Column />
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column>
                        <Checkbox
                          label={t('EHS Sito')}
                          checked={
                            values.userRoles.includes(EUserRoles.EhsManager) ||
                            values.userRoles.includes(EUserRoles.EhsApplicant)
                          }
                          onClick={(event, data) =>
                            this.handleUserRoleChange(
                              values.userRoles,
                              setFieldValue,
                              EUserRoles.EhsManager,
                              data.checked
                            )
                          }
                        />
                      </Grid.Column>
                      <Grid.Column>
                        <Select
                          value={
                            values.userRoles.find((userRole) => EhsSiteRoles.includes(userRole)) ??
                            EUserRoles.EhsManager
                          }
                          onChange={(event, data) =>
                            this.handleUserRoleChange(values.userRoles, setFieldValue, data.value as EUserRoles, true)
                          }
                          disabled={!values.userRoles.find((userRole) => EhsSiteRoles.includes(userRole))}
                          options={[
                            {
                              value: EUserRoles.EhsManager,
                              text: t('EHS Sito Manager'),
                            },
                            {
                              value: EUserRoles.EhsApplicant,
                              text: t('EHS Sito Applicant'),
                            },
                          ]}
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column>
                        <Checkbox
                          label={t('EHS Maintenance')}
                          checked={
                            values.userRoles.includes(EUserRoles.EhsMaintenanceManager) ||
                            values.userRoles.includes(EUserRoles.EhsMaintenanceBasic) ||
                            values.userRoles.includes(EUserRoles.EhsMaintenanceExternalCompany) ||
                            values.userRoles.includes(EUserRoles.EhsMaintenanceExternalCompanyPlus)
                          }
                          onClick={(event, data) =>
                            this.handleUserRoleChange(
                              values.userRoles,
                              setFieldValue,
                              EUserRoles.EhsMaintenanceManager,
                              data.checked
                            )
                          }
                        />
                      </Grid.Column>
                      <Grid.Column>
                        <Select
                          value={
                            values.userRoles.find((userRole) => EhsMaintenanceRoles.includes(userRole)) ??
                            EUserRoles.EhsMaintenanceManager
                          }
                          onChange={(event, data) =>
                            this.handleUserRoleChange(values.userRoles, setFieldValue, data.value as EUserRoles, true)
                          }
                          disabled={!values.userRoles.find((userRole) => EhsMaintenanceRoles.includes(userRole))}
                          options={[
                            {
                              value: EUserRoles.EhsMaintenanceManager,
                              text: t('EHS Maintenance Manager'),
                            },
                            {
                              value: EUserRoles.EhsMaintenanceBasic,
                              text: t('EHS Maintenance Basic'),
                            },
                            {
                              value: EUserRoles.EhsMaintenanceExternalCompany,
                              text: t('EHS Maintenance External Company'),
                            },
                            {
                              value: EUserRoles.EhsMaintenanceExternalCompanyPlus,
                              text: t('EHS Maintenance External Company Plus'),
                            },
                          ]}
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                  {serverRequest && serverRequest.success && <Message content={t('User updated')} />}
                  {serverRequest && serverRequest.error && <Message error content={serverRequest.error} />}
                </Modal.Description>
              </Form>
            </Modal.Content>
            <Modal.Actions>
              <Button onClick={this.handleClose}>
                <TranslationComponent stringKey={'Cancel'} />
              </Button>
              <Button primary form="editUserForm" type="submit">
                <TranslationComponent stringKey={'Confirm'} />
              </Button>
            </Modal.Actions>
          </Modal>
        )}
      </Formik>
    );
  }
}

const mapStateToProps = (state: RootState) => ({ serverRequest: appStateServerRequests(state, ActionType.USER_EDIT) });

export const UserEditModal = connect(mapStateToProps, { userEditAction })(withTranslation()(PUserEditModal));
